<template>
  <div class="tdh">
    <!-- SAMPLE PROJECT RIBBON -->
    <sample-ribbon
      v-if="!isDataLoading && $permissions.isSampleInternalProjectID(projectID)"
      size="small"
    />
    <!-- DETAILS LOADING -->
    <task-detail-loading v-if="isDataLoading" />
    <template v-else>
      <!-- HEADER TOP BAR -->
      <div class="tdh__topbar">
        <div v-if="$screen.isTabletOrDesktop($mq)" class="tdh__topbar--project-title">
          <i class="ddi ddi-project" />
          <router-link 
            :to="RouteAssistant.getProjectLandingRoute($route.params.accountId, projectID, $permissions)" 
            class="just-a-link">{{ projectName }}</router-link>
        </div>
        <task-detail-meta-options
          :global-attachments="globalAttachments"
          :is-pinned="isPinned"
          :show-harvest-option="showHarvestOption"
          class="marker--tdh-topbar--task-detail-meta-options"
          @show-harvest-modal="$emit('show-harvest-modal')"
          @move-clicked="$emit('move-clicked')"
          @delete-clicked="$emit('delete-clicked')"
          @pin-toggled="$emit('pin-toggled')"
        />
      </div>
      <div class="tdh__main">
        <div class="body">
          <!-- DESKTOP: META INFO HEADER -->
          <div v-if="$screen.isDesktop($mq)" class="marker--desktop-meta-info-header desktop-meta-info-header">
            <div class="ref-number">
              #{{refNumber}}
            </div>
            <div>
              <priority-picker
                v-model:selected-id="internalPriorityID"
                :is-disabled="$permissions.isGuest"
                :options="priorityOptions"
              />
            </div>
            <div>
              <due-date-picker v-model="internalDueDate" :is-disabled="$permissions.isGuest" />
            </div>
          </div>
          <!-- TITLE -->
          <div prop="title" class="title-wrapper">
            <masthead-input
              ref="title"
              v-model="internalTitle"
              :is-disabled="$permissions.isGuest"
              :class="{'title--focus': titleIsFocus }"
              class="title"
              placeholder="Give this a title…"
              maxlength="255"
              @focus="titleIsFocus = true"
              @blur="titleIsFocus = false"
              @input-submitted="updateTitle"
            />
          </div>
          <!-- DESKTOP: CUSTOM FIELDS -->
          <custom-field-values
            v-if="$screen.isDesktop($mq)"
            ref="customFieldValues"
            :entity-id="projectID"
            :item-id="taskID"
            entity-type="project"
            @value-updated="$emit('update-custom-field')"
          />
          <!-- TABLET OR MOBILE: FIRST MESSAGE HEADER (desktop shows this in the side options, like conversations) -->
          <div v-if="$screen.isTabletOrMobile($mq)" class="creator-line">
            <span>
              <router-link v-if="scheduledTaskRoute" :to="scheduledTaskRoute" class="just-a-link">{{ creatorName }}</router-link>
              <template v-else>{{ creatorName }}</template>
              on {{ TimeAssistant.formatDateToDateAndTime(creationDate) }}
            </span>
          </div>
          <!-- TABLET OR MOBILE: META INFO HEADER -->
          <template v-if="$screen.isTabletOrMobile($mq)">
            <div class="tablet-mobile-meta-info-header">
              <div class="job-assignments">
                <!-- Jobs enabled -->
                <template v-if="hasJobs">
                  <div
                    v-for="(jobAppointee, index) in jobInfo.jobAppointees" 
                    :key="index" 
                    :class="{'selected': isJobAppointeeAssigned(jobAppointee)}"
                  >
                    <assignment-picker
                      v-model:selected-id="jobAppointeeMaps[index].appointeeID" 
                      :options="assembleAppointeeOptions(jobAppointee.appointee)"
                      :is-disabled="$permissions.isGuest"
                      :size="tabletMobilePickerSize"
                      :title="jobAppointee.job.name"
                    />
                  </div>
                </template>
              </div>
              <div class="meta-info">
                <!-- Assignee only -->
                <assignment-picker
                  v-if="!hasJobs"
                  v-model:selected-id="internalAssigneeID"
                  :options="assigneesOptions"
                  :is-disabled="$permissions.isGuest && !isAssignedToSelf"
                  :size="tabletMobilePickerSize"
                  hide-name
                />
                <status-picker 
                  v-model:selected-id="internalStatusID"
                  :is-disabled="$permissions.isGuest && !isAssignedToSelf" 
                  :statuses="statusOptions" 
                  :job-info="jobInfo" 
                  :size="tabletMobilePickerSize"
                />
                <priority-picker
                  v-model:selected-id="internalPriorityID"
                  :is-disabled="$permissions.isGuest" 
                  :options="priorityOptions"
                  :size="tabletMobilePickerSize"
                />
                <due-date-picker
                  v-model="internalDueDate"
                  :is-disabled="$permissions.isGuest"
                  :size="tabletMobilePickerSize"
                />
              </div>
            </div>
            <!-- TABLET OR MOBILE: CUSTOM FIELDS (sits above the description) -->
            <custom-field-values
              ref="customFieldValues"
              :entity-id="projectID"
              :item-id="taskID"
              entity-type="project"
              class="tablet-mobile-custom-fields"
              @value-updated="$emit('update-custom-field')"
            />
            <!-- TABLET OR MOBILE: TAGS (sits below the custom fields) -->
            <div class="tablet-mobile-tags-holder">
              <tags-input
                v-model:selected-tags="internalListTags"
                :is-disabled="$permissions.isGuest"
                :hide-icon="internalListTags.length > 0"
                :available-tags="tagsOptions"
              />
            </div>
          </template>
          <!-- DESCRIPTION -->
          <task-detail-header-description
            :is-disabled="$permissions.isGuest"
            :description="internalDescription"
            :source-email-link="sourceEmailLink"
            :initial-attachments="initialAttachments"
            :mention-users="mentionUsers"
            :saved-templates="savedTemplates"
            @update-description="(newVal) => $emit('update-description', newVal)"
          />
          <!-- RELATED ITEMS -->
          <div v-if="$permissions.isNotGuest" class="related-items">
            <div class="related-items__nav">
              <div
                :class="{'selected' : selectedTaskType === 0}"
                @click="selectedTaskType=0"
              ><i class="ddi ddi-everything" />&nbsp;{{ checklistsButtonLabel }}</div>
              <div
                :class="{'selected' : selectedTaskType === 1}"
                @click="selectedTaskType=1"
              ><i class="ddi ddi-task" />&nbsp;{{ linkedTasksButtonLabel }}</div>
              <div
                :class="{'selected' : selectedTaskType === 2}"
                @click="selectedTaskType=2"
              ><i class="ddi ddi-conversation" />&nbsp;{{ linkedConversationsButtonLabel }}</div>
              <div placement="center">
                <checklist-picker
                  v-if="selectedTaskType === 0" 
                  :project-id="projectID" 
                  :item-id="taskID" 
                  @checklist-created="(checklist) => handleChecklistCreated(checklist)" 
                />
                <dd-icon-button v-if="selectedTaskType === 1" icon="task-add" type="circle" size="small" color="green" @click="openLinkTasksDrawer()"/>
                <dd-icon-button v-if="selectedTaskType === 2" icon="plus" type="circle" size="small" color="green" @click="openLinkConversationsDrawer()"/>
              </div>
            </div>
            <!-- CHECKLISTS -->
            <checklist-items-list
              v-show="selectedTaskType === 0"
              :checklists="checklists"
              @checklist-deleted="(checklist) => handleChecklistDeleted(checklist)"
              @checklist-created="(checklist) => handleChecklistCreated(checklist)" 
            />
            <!-- LINKED TASKS -->
            <related-items-list
              v-show="selectedTaskType === 1"
              :items="linkedTasks"
              :entity-type="EEntities.PROJECTS"
              group-by-entity
              add-link-text="Add Linked Task"
              @add-link-clicked="openLinkTasksDrawer()"
              @item-clicked="(item) => selectLinkedTask(item.id)"
            />
            <!-- LINKED CONVERSATIONS -->
            <related-items-list
              v-show="selectedTaskType === 2"
              :items="linkedConversations"
              :entity-type="EEntities.MAILBOXES"
              group-by-entity
              add-link-text="Link Conversation"
              @add-link-clicked="openLinkConversationsDrawer()"
              @item-clicked="(item) => selectLinkedConversation(item.id)"
            />
          </div>
        </div>
        <!-- DESKTOP: SIDE OPTIONS -->
        <div v-if="$screen.isDesktop($mq)" class="marker--status-assignment-options options">
          <div>
            <i class="ddi ddi-workflows" />
            <status-picker
              v-model:selected-id="internalStatusID"
              :is-disabled="$permissions.isGuest && !isAssignedToSelf"
              :statuses="statusOptions"
              :job-info="jobInfo"
            />
          </div>
          <!-- Jobs enabled -->
          <div v-if="hasJobs" class="job-assignments">
            <i class="ddi ddi-unassigned" />
            <div
              v-for="(jobAppointee, index) in jobInfo.jobAppointees" 
              :key="index" 
              :class="{'selected': isJobAppointeeAssigned(jobAppointee)}"
            >
              <i v-if="isJobAppointeeAssigned(jobAppointee)" class="ddi ddi-check" />
              <span class="label">
                {{ jobAppointee.job.name }}
              </span>
              <assignment-picker
                v-model:selected-id="jobAppointeeMaps[index].appointeeID"  
                :is-disabled="$permissions.isGuest" 
                :options="assembleAppointeeOptions(jobAppointee.appointee)"
              />
            </div>
          </div>
          <!-- Assignee only -->
          <div v-else>
            <i class="ddi ddi-unassigned" />
            <assignment-picker
              v-model:selected-id="internalAssigneeID"
              :is-disabled="$permissions.isGuest && !isAssignedToSelf"
              :options="assigneesOptions"
            />
          </div>
          <!-- TAGS -->
          <div class="tags-holder">
            <i class="ddi ddi-tag" />
            <tags-input
              v-model:selected-tags="internalListTags"
              :is-disabled="$permissions.isGuest"
              :available-tags="tagsOptions"
              hide-icon
            />
          </div>
          <!-- FIRST MESSAGE HEADER -->
          <div class="creator-line">
            Created by
            <router-link v-if="scheduledTaskRoute" :to="scheduledTaskRoute" class="just-a-link">{{ creatorName }}</router-link>
            <template v-else>{{ creatorName }}</template><br>
            {{ TimeAssistant.formatDateToDateAndTime(creationDate) }}
          </div>
        </div>
      </div>
    </template>

    <!-- LINK TASKS DRAWER -->
    <link-tasks-drawer
      v-model:show-drawer="showLinkTasksDrawer"
      :currently-linked-tasks="linkedTasks"
      :originating-task="taskForLinking"
      :default-project-id="projectID"
      :originating-item-event-id="originatingEventID"
      @task-created="$emit('linked-tasks-updated', false)"
      @selected-existing-task-id="(id) => linkExistingTask(id)"
    />

    <!-- LINK CONVERSATIONS DRAWER -->
    <link-conversations-drawer
      v-if="$permissions.isNotGuest"
      v-model:show-drawer="showLinkConversationsDrawer"
      :currently-linked-conversations="linkedConversations"
      :originating-task="taskForLinking"
      @selected-existing-conversation-id="(id) => linkExistingConversationId(id)"
    />

    <!-- TASK DETAIL DRAWER -->
    <task-detail-drawer
      v-model:show-drawer="showTaskDetailDrawer"
      :originating-item="taskForLinking"
      :task="selectedLinkedTask"
      :prev-item="prevLinkedItem"
      :next-item="nextLinkedItem"
      @navigate="(item) => selectLinkedTask(item.itemID)"
      @unlink-task="unlinkSelectedLinkedTask"
    />

    <!-- CONVERSATION DETAIL DRAWER -->
    <conversation-detail-drawer
      v-if="$permissions.isNotGuest"
      v-model:show-drawer="showConversationDetailDrawer"
      :originating-task="taskForLinking"
      :conversation="selectedLinkedConversation"
      :prev-item="prevLinkedItem"
      :next-item="nextLinkedItem"
      @navigate="(item) => selectLinkedConversation(item.itemID)"
      @unlink-conversation="unlinkSelectedLinkedConversation"
    />
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { cloneDeep } from 'lodash-es'

/* Mixins */

/* Components */
import TaskDetailLoading from './task-detail-loading.vue'
import TaskDetailMetaOptions from './task-detail-meta-options.vue'
import TaskDetailHeaderDescription from './task-detail-header-description.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import TimeAssistant from '@/helpers/time-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IChecklist,
  IDescriptionTemplate,
  IItemReference,
  IJobAppointee,
  ILinkedItemForListing,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IScheduledTaskReference,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IJobAppointeeMap, IJobInfo } from '@/types/data-objects/items'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import { useAppStore } from '@/modules/app/app.store'

enum RelatedTaskTypes {
  Checklists = 0,
  Tasks = 1,
  Conversations = 2,
}

export default defineComponent({
  name: 'TaskDetailHeader',

  components: {
    TaskDetailLoading,
    TaskDetailMetaOptions,
    TaskDetailHeaderDescription,
  },

  props: {
    projectName: {
      type: String,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    assigneesOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    tagsOptions: {
      type: Array as () => Array<string>,
      required: true,
    },

    linkedTasks: {
      type: Array as () => ILinkedItemForListing[],
      default: (): ILinkedItemForListing[] => [],
    },

    linkedConversations: {
      type: Array as () => ILinkedItemForListing[],
      default: (): ILinkedItemForListing[] => [],
    },

    checklists: {
      type: Array as () => IChecklist[],
      default: (): IChecklist[] => [],
    },

    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    initialAttachments: {
      type: Array as () => IAttachment[],
      default: (): IAttachment[] => [],
    },

    globalAttachments: {
      type: Array as () => IAttachment[],
      required: true,
    },

    savedTemplates: {
      type: Array as () => IDescriptionTemplate[],
      default: (): IDescriptionTemplate[] => [],
    },

    refNumber: {
      type: Number,
      required: true,
    },

    description: {
      type: String,
      required: true,
    },

    sourceEmailLink: {
      type: String,
      default: null,
    },

    creationDate: {
      type: [Date, String],
      required: true,
    },

    dueDate: {
      type: [Date, String],
      default: null,
    },

    creator: {
      type: Object as () => IPersonWithEmail,
      required: true,
    },

    // Set when a scheduled task created this task
    scheduledTask: {
      type: Object as () => IScheduledTaskReference,
      default: null,
    },

    assigneeID: {
      type: Number,
      default: null,
    },

    priorityID: {
      type: Number,
      required: true,
    },

    statusID: {
      type: Number,
      required: true,
    },

    title: {
      type: String,
      required: true,
    },

    listTags: {
      type: Array as () => Array<string>,
      default: (): Array<string> => [],
    },

    showHarvestOption: {
      type: Boolean,
      required: true,
    },

    isDataLoading: {
      type: Boolean,
      default: false,
    },

    isPinned: {
      type: Boolean,
      required: true,
    },
  },

  emits: [
    'delete-clicked',
    'linked-conversations-updated',
    'linked-tasks-updated',
    'move-clicked',
    'pin-toggled',
    'show-harvest-modal',
    'update-assignee',
    'update-custom-field',
    'update-description',
    'update-dueDate',
    'update-job-appointee',
    'update-priority',
    'update-status',
    'update-tags',
    'update-title',
  ],

  data() {
    return {
      // Read once, not computed from $route, which has the next page's params by the time a late save finishes
      projectID: Number(this.$route.params.id),
      taskID: Number(this.$route.params.childId),
      EEntities,
      RouteAssistant,
      TimeAssistant,
      internalTitle: '',
      internalDescription: '',

      // UI controls
      titleIsFocus: false,
      showModalHarvest: false,
      isUpdatingTitle: false,
      selectedTaskType: RelatedTaskTypes.Tasks,
      tabletMobilePickerSize: 'medium',

      // Link task drawer
      showLinkTasksDrawer: false,
      originatingEventID: null as any,

      // Link conversations drawer
      showLinkConversationsDrawer: false,

      // Task detail drawer
      showTaskDetailDrawer: false,
      selectedLinkedTask: {} as ILinkedItemForListing,

      // Convo detail drawer
      showConversationDetailDrawer: false,
      selectedLinkedConversation: {} as ILinkedItemForListing,

      // Used to display previous/next links on drawers...
      nextLinkedItem: {} as ILinkedItemForListing,
      prevLinkedItem: {} as ILinkedItemForListing,

      // Job appointee data
      jobAppointeeMaps: [] as IJobAppointeeMap[],
      // Store to check differences on watching jobAppointeeMaps[i]
      // since old array is not tracked on inner mutations...
      previousJobAppointeeMaps: [] as IJobAppointeeMap[],
    }
  },

  computed: {
    ...mapState(useAppStore, ['itemEventForLinkedTaskID']),
    taskForLinking(): IItemReference {
      return {
        id: this.taskID,
        title: this.title,
        refNumber: this.refNumber,
      } as IItemReference
    },
    // A task made by a scheduled task shows that as its creator
    creatorName(): string {
      return this.scheduledTask ? this.scheduledTask.name : this.creator.name
    },
    // Opens the scheduled task on the occurrence this task was created for. It's in the scheduled task's own project,
    // which differs from the task's if the task was moved.
    scheduledTaskRoute(): any {
      if (!this.scheduledTask || !this.scheduledTask.canOpen) {
        return null
      }
      const query: { scheduledTaskId: string; date?: string } = {
        scheduledTaskId: this.scheduledTask.id.toString(),
      }
      if (this.scheduledTask.eventDate) {
        query.date = this.scheduledTask.eventDate.slice(0, 10)
      }
      return {
        ...RouteAssistant.getProjectScheduleRoute(
          this.$route.params.accountId,
          this.scheduledTask.projectID,
        ),
        query,
      }
    },
    linkedConversationsButtonLabel(): string {
      return 'Conversations (' + this.linkedConversations.length + ')'
    },
    linkedTasksButtonLabel(): string {
      return 'Tasks (' + this.linkedTasks.length + ')'
    },
    checklistsButtonLabel(): string {
      return (
        'Checklists (' +
        this.checklists.reduce(
          (p, c) =>
            p +
            (c.checklistItems || []).reduce(
              (p2, c2) => p2 + (c2.isDone ? 1 : 0),
              0,
            ),
          0,
        ) +
        '/' +
        this.checklists.reduce(
          (p, c) => p + (c.checklistItems || []).length,
          0,
        ) +
        ')'
      )
    },
    hasSelectedTags(): boolean {
      return this.listTags.length > 0
    },
    hasJobs(): boolean {
      return this.jobInfo !== null
    },
    internalStatusID: {
      get(): any {
        return this.statusID
      },
      set(newValue: any) {
        if (this.statusID !== newValue) {
          this.$emit('update-status', newValue)
        }
      },
    },

    internalPriorityID: {
      get(): any {
        return this.priorityID
      },
      set(newValue: any) {
        if (this.priorityID !== newValue) {
          this.$emit('update-priority', newValue)
        }
      },
    },

    internalDueDate: {
      get(): any {
        return this.dueDate
      },
      set(newValue: any) {
        if (this.dueDate !== newValue) {
          this.$emit('update-dueDate', newValue)
        }
      },
    },

    internalAssigneeID: {
      get(): any {
        return this.assigneeID
      },
      set(newValue: any) {
        if (this.assigneeID !== newValue) {
          this.$emit('update-assignee', newValue)
        }
      },
    },

    isAssignedToSelf(): boolean {
      return this.internalAssigneeID === this.$permissions.loggedInUser.id
    },

    internalListTags: {
      get(): any {
        return this.listTags
      },
      set(newValue: any) {
        this.$emit('update-tags', newValue)
      },
    },
  },

  watch: {
    itemEventForLinkedTaskID: {
      handler(val) {
        if (val !== null) {
          this.openLinkTasksDrawer(val)
          useAppStore().itemEventForLinkedTaskID = null
        }
      },
    },

    title: {
      immediate: true,
      handler(newVal: string) {
        this.internalTitle = newVal
        this.isUpdatingTitle = false
      },
    },

    description: {
      immediate: true,
      handler(newVal: string) {
        this.internalDescription = newVal
      },
    },

    'jobInfo.jobAppointees': {
      immediate: true,
      handler(newVal: IJobAppointee[]) {
        if (!newVal) {
          return
        }
        newVal.forEach(j => {
          this.jobAppointeeMaps.push({
            jobID: j.job.id,
            appointeeID: j.appointee !== null ? j.appointee.id : null,
          })
        })
        this.previousJobAppointeeMaps = cloneDeep(this.jobAppointeeMaps)
      },
    },

    jobAppointeeMaps: {
      async handler(newJobAppointeeMaps) {
        for (let i = 0; i < newJobAppointeeMaps.length; i++) {
          if (
            newJobAppointeeMaps[i].appointeeID !==
            this.previousJobAppointeeMaps[i].appointeeID
          ) {
            this.$emit(
              'update-job-appointee',
              newJobAppointeeMaps[i].jobID,
              newJobAppointeeMaps[i].appointeeID,
            )
          }
        }
        this.previousJobAppointeeMaps = cloneDeep(newJobAppointeeMaps)
      },
      deep: true,
    },

    checklists: {
      deep: 1,
      immediate: true,
      handler() {
        if (this.checklists.length > 0) {
          this.selectedTaskType = RelatedTaskTypes.Checklists
        }
      },
    },
  },

  methods: {
    selectLinkedTask(taskID: number) {
      const itemIndex = this.linkedTasks.map(i => i.id).indexOf(taskID)
      this.selectedLinkedTask = this.linkedTasks[itemIndex]
      this.setNextPrevLinkedItems(this.linkedTasks, itemIndex)
      this.showTaskDetailDrawer = true
    },

    selectLinkedConversation(conversationID: number) {
      const itemIndex = this.linkedConversations
        .map(i => i.id)
        .indexOf(conversationID)
      this.selectedLinkedConversation = this.linkedConversations[itemIndex]
      this.setNextPrevLinkedItems(this.linkedConversations, itemIndex)
      this.showConversationDetailDrawer = true
    },

    isJobAppointeeAssigned(jobAppointee: IJobAppointee): boolean {
      const mandate = this.jobInfo.assigneeMandates.find(
        am => am.statusID === this.statusID,
      )
      if (mandate) {
        const jobIDMandated = mandate.jobID

        if (jobAppointee.job.id !== jobIDMandated) {
          return false
        } else {
          if (
            (this.assigneeID === null && jobAppointee.appointee === null) ||
            (jobAppointee.appointee !== null &&
              this.assigneeID === jobAppointee.appointee.id)
          ) {
            return true
          }
        }
      }

      return false
    },

    // Ensures the job appointee is part of the list of options
    // (since we are simply pulling from the list of available assignees
    // it's possible the current job appointee was removed from the project
    // and never updated.)
    // May want to pull back a separate thing from getting listAssignees since
    // that is all memberships + current assignee -- so may be weird to see that.
    // Or just leave it. kinda edge casy.
    assembleAppointeeOptions(appointee: IPersonWithEmail): IPersonWithEmail[] {
      if (
        appointee === null ||
        this.assigneesOptions.find(a => a.id === appointee.id)
      ) {
        return this.assigneesOptions
      }
      return [...this.assigneesOptions, appointee]
    },

    async unlinkSelectedLinkedTask() {
      try {
        const response = await TasksAPI.put_UnlinkTask(
          this.projectID,
          this.taskID,
          this.selectedLinkedTask.id,
        )
        this.$messaging.showSuccess(response.message)
        this.$emit('linked-tasks-updated', false)
        this.showTaskDetailDrawer = false
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async unlinkSelectedLinkedConversation() {
      try {
        const response = await TasksAPI.put_UnlinkConversation(
          this.projectID,
          this.taskID,
          this.selectedLinkedConversation.id,
        )
        this.$messaging.showSuccess(response.message)
        this.$emit('linked-conversations-updated')
        this.showConversationDetailDrawer = false
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async linkExistingTask(taskId: number) {
      try {
        const response = await TasksAPI.post_LinkExistingTasks(
          this.projectID,
          this.taskID,
          [taskId],
        )
        this.$messaging.showSuccess(response.message)
        this.$emit('linked-tasks-updated', false)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async linkExistingConversationId(conversationId: number) {
      try {
        const response = await TasksAPI.post_LinkExistingConversations(
          this.projectID,
          this.taskID,
          [conversationId],
        )
        this.$messaging.showSuccess(response.message)
        this.$emit('linked-conversations-updated')
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async openLinkTasksDrawer(originatingEventID: number = null) {
      this.originatingEventID = originatingEventID
      this.showLinkTasksDrawer = true
    },

    async handleChecklistCreated(checklist: IChecklist) {
      this.checklists.push(checklist)
    },

    async handleChecklistDeleted(checklist: IChecklist) {
      this.checklists.splice(
        this.checklists.findIndex(i => i.id === checklist.id),
        1,
      )
    },

    async openLinkConversationsDrawer() {
      this.showLinkConversationsDrawer = true
    },

    // These are for the next/prev links on the drawer, not to be confused with the ones on the base header
    setNextPrevLinkedItems(
      linkedItems: ILinkedItemForListing[],
      itemIndex: number,
    ) {
      this.prevLinkedItem = null
      this.nextLinkedItem = null

      if (itemIndex > 0) {
        this.prevLinkedItem = linkedItems[itemIndex - 1]
      }

      if (itemIndex < linkedItems.length - 1) {
        this.nextLinkedItem = linkedItems[itemIndex + 1]
      }
    },

    /**
     * Handle save event and avoid making requests if the value doesn't change
     */
    updateTitle(newValue: string) {
      if (newValue.trim().length === 0) {
        this.internalTitle = this.title
        return
      }
      if (this.title !== newValue && !this.isUpdatingTitle) {
        this.isUpdatingTitle = true
        this.$emit('update-title', newValue)
      }
    },
  },
})
</script>

<style lang="scss">
.tdh {
  .sample-ribbon {
    top: 20px;
    left: 10px;

    @include media-breakpoint-up(md) {
      top: 64px;
      left: -20px;
    }
  }

  &__topbar {
    position: relative;
    width: 100%;
    background-color: var(--color-background-blue);
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    border-bottom: $border--dotted-light;

    @include media-breakpoint-down(sm) {
      padding: 0 $detail-gutter-pad-sm;
      background-color: transparent;
      border: none;
      justify-content: flex-end;
    }

    &--project-title {
      @include lettering--p-sm;
      @include single-line-ellipsis;

      .ddi-project {
        color: $color--blue-80;
        padding-right: 8px;
      }
    }
  }

  &__main {
    display: flex;
    @include flex-row-horizontal-spacing(0.5 * $detail-gutter-pad-lg);
    margin-top: 30px;
    padding: 0 0 0 0.5 * $detail-gutter-pad-lg;

    .masthead-input {
      .el-textarea {
        &__inner {
          font-size: 24px !important;
        }
      }
    }

    @include media-breakpoint-down(md) {
      padding: 0 $detail-gutter-pad-md;
    }

    @include media-breakpoint-down(sm) {
      margin-top: 0;
      padding: 0 $detail-gutter-pad-sm;
    }

    .body {
      @include media-breakpoint-up(lg) {
        flex: 1;
      }

      width: 100%;
      // No wider than the space beside the options, whatever it holds (a wide table in the description scrolls or
      // shrinks)
      min-width: 0;

      .desktop-meta-info-header {
        display: flex;
        @include flex-row-horizontal-spacing(10px);
        @include flex-row-vertical-spacing-on-wrap(10px);

        > .ref-number {
          @include lettering--h4;
          padding-top: 2px;
        }
      }

      .tablet-mobile-tags-holder {
        margin: 8px 0 0 -6px;
      }

      .tablet-mobile-custom-fields {
        margin-top: 10px;
      }

      .tablet-mobile-meta-info-header {
        margin-top: 10px;
        display: block;
        background-color: var(--color-background-primary);
        border: $border--solid-light;
        padding: 12px;
        border-radius: 8px;

        > .job-assignments {
          display: flex;
          justify-content: center;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
          margin-bottom: 8px;
        }

        > .meta-info {
          display: flex;
          justify-content: center;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
        }
      }

      .title-wrapper {
        margin-bottom: 10px;

        .title {
          width: calc(100% + 30px);
          margin-left: -15px;
          border: 6px solid transparent;

          > div {
            padding: 0 8px;

            .el-textarea__main {
              padding: 0;
            }
          }

          @include media-breakpoint-up(md) {
            &:hover {
              background-color: var(--color-background-secondary);
              border-radius: 8px;
            }
          }

          &--focus {
            width: calc(100% + 22px);
            margin-top: 4px;
            margin-bottom: 14px;
            margin-left: -11px;
            border: solid 2px $projects--theme-color;
            border-radius: 8px;

            &:hover {
              background: var(--color-background-secondary);
              border-radius: 8px;
            }
          }
        }
      }

      .creator-line {
        display: flex;
        @include lettering--p-sm;
        color: var(--color-text-primary);
      }

      .related-items {
        margin-top: 20px;
        margin-bottom: 48px;

        &__nav {
          display: flex;
          padding: 0 1px;

          > div {
            padding: 8px 0 2px 0;
            margin: 0 12px;
            display: inline-block;
            cursor: pointer;
            @include lettering--p-sm;
            color: var(--color-text-third);
            font-weight: $bold;

            /* Add related items button */
            &:last-child {
              align-self: center;
              margin-left: auto !important;
              padding: 6px 0;
            }

            &.selected {
              color: var(--color-text-secondary);
              border-bottom: solid 2px $projects--theme-color;
            }
          }
        }
      }
    }

    .options {
      max-width: 240px;
      min-width: 240px;
      /* Aligns with bottom of first line of title */
      margin-top: 40px;
      margin-bottom: 40px;
      display: flex;
      flex-direction: column;

      $options-top-padding: 16px;

      > div {
        padding: $options-top-padding 10px;
        border-top: $border--dotted-light;
        position: relative;

        > .ddi {
          position: absolute;
          font-size: 14px;
          right: 15px;
          top: -12px;
          background-color: var(--color-background-header);
          padding: 4px;
          color: var(--color-text-third);
        }

        .label {
          display: block;
          @include lettering--p-sm;
          @include single-line-ellipsis;
          margin-bottom: 4px;
        }
      }

      > .tags-holder {
        .tags-input {
          margin-left: -6px;
          margin-top: -4px;
        }
      }

      .creator-line {
        @include lettering--p-sm;
        @include single-line-ellipsis;
        color: var(--color-text-primary);
        border-top: $border--dotted-light;
        padding-top: 14px;
      }

      > .job-assignments {
        padding: 0;
        padding: ($options-top-padding - 14px) 0;

        > div {
          position: relative;
          margin: 10px 0;
          padding: 7px 10px;

          &.selected {
            background-color: var(--color-background-primary);
            border: $border--dotted-light;
            border-right: none;
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;

            .label {
              font-weight: $bold;
            }
          }

          .ddi-check {
            position: absolute;
            top: 10px;
            right: 20px;
            font-size: 14px;
            color: $color--green;
          }
        }
      }
    }
  }
}
</style>
